import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Mail, X, ZoomIn } from 'lucide-react';
import { useAbTest, useAnalytics } from '../hooks/useAnalytics';
import { t } from './LanguagePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BOOK_COVER_URL = `${BACKEND_URL}/api/static/book-cover.png`;

const BookZoomModal = ({ isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale(prev => Math.min(5, Math.max(0.5, prev + (e.deltaY > 0 ? -0.2 : 0.2))));
  }, []);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({ x: touch.clientX - dragStart.x, y: touch.clientY - dragStart.y });
  };

  useEffect(() => {
    if (!isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="zoom-modal-overlay" onClick={onClose} data-testid="zoom-modal">
      <div className="zoom-modal-controls">
        <button onClick={(e) => { e.stopPropagation(); setScale(s => Math.min(5, s + 0.5)); }} data-testid="zoom-in-btn">+</button>
        <span>{Math.round(scale * 100)}%</span>
        <button onClick={(e) => { e.stopPropagation(); setScale(s => Math.max(0.5, s - 0.5)); }} data-testid="zoom-out-btn">-</button>
        <button onClick={onClose} className="zoom-close-btn" data-testid="zoom-close-btn"><X size={20} /></button>
      </div>
      <div
        className="zoom-modal-content"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <img
          src={BOOK_COVER_URL}
          alt="Atomic Habits for Sharia Bankers - Full Cover"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 0.2s ease',
            maxHeight: '90vh',
            maxWidth: '90vw',
            objectFit: 'contain',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
          draggable={false}
          data-testid="zoom-book-image"
        />
      </div>
    </div>
  );
};

const HeroSection = ({ onEmailSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [showZoom, setShowZoom] = useState(false);
  const [heroContent, setHeroContent] = useState({
    title: "BERANI\nMERASA,\nBERANI\nMEMIMPIN",
    subtitle: "Transformasi Kepemimpinan Syariah Dimulai dari Kebiasaan Kecil",
  });
  const [variant, setVariant] = useState(null);
  const { fetchVariant } = useAbTest('hero_headline');
  const { trackConversion } = useAnalytics();

  useEffect(() => {
    fetchVariant().then((data) => {
      if (data.content) setHeroContent(data.content);
      setVariant(data.variant);
    });
  }, [fetchVariant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setMessage('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscribe`, { email, source: 'hero' });
      setMessage(response.data.message);
      setEmail('');
      trackConversion('hero_subscribe');
      if (onEmailSubmit) onEmailSubmit(email);
    } catch (error) {
      setMessage(error.response?.data?.detail || t('general_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleLines = heroContent.title.split('\n');

  return (
    <section className="hero-section" data-testid="hero-section" data-variant={variant}>
      <div className="hero-background"><div className="hero-overlay"></div></div>
      <div className="hero-content">
        <div className="hero-grid">
          <div className="hero-text">
            <span className="hero-label">{t('lp_hero_label')}</span>
            <h1 className="hero-title" data-testid="hero-title">
              {titleLines.map((line, i) => (
                <React.Fragment key={i}>{line}{i < titleLines.length - 1 && <br />}</React.Fragment>
              ))}
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">{t('lp_hero_subtitle')}</p>
            <p className="hero-description">
              {t('lp_hero_desc')}
            </p>
            <div className="hero-cta">
              <form onSubmit={handleSubmit} className="email-capture-form" data-testid="hero-email-form">
                <div className="input-group">
                  <Mail size={20} />
                  <input type="email" placeholder={t('lp_hero_email_ph')} value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isSubmitting} data-testid="hero-email-input" />
                </div>
                <button type="submit" className="btn-primary" disabled={isSubmitting} data-testid="hero-submit-btn">
                  {isSubmitting ? t('lp_hero_submitting') : t('lp_hero_cta')} <ArrowRight size={20} />
                </button>
              </form>
              {message && <p className="form-message" data-testid="hero-form-message">{message}</p>}
            </div>
            <div className="hero-stats">
              <div className="stat-item"><span className="stat-number">144</span><span className="stat-label">{t('lp_hero_pages')}</span></div>
              <div className="stat-item"><span className="stat-number">15</span><span className="stat-label">{t('lp_hero_chapters')}</span></div>
              <div className="stat-item"><span className="stat-number">6</span><span className="stat-label">{t('lp_hero_experts')}</span></div>
            </div>
          </div>
          <div className="hero-book-visual">
            <div className="book-showcase" onClick={() => setShowZoom(true)} data-testid="book-showcase">
              <img src={BOOK_COVER_URL} alt="Atomic Habits for Sharia Bankers" className="book-cover-main" data-testid="hero-book-cover" />
              <div className="book-glow"></div>
              <div className="book-zoom-hint"><ZoomIn size={18} /> {t('lp_hero_zoom')}</div>
            </div>
            <p className="hero-quote">{t('lp_hero_quote')}</p>
          </div>
        </div>
      </div>
      <BookZoomModal isOpen={showZoom} onClose={() => setShowZoom(false)} />
    </section>
  );
};

export default HeroSection;
